import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Trophy, Lock, LogOut, Search, Download, Upload, Users, Eye, Trash2, ArrowLeft, LayoutGrid, Save, FolderOpen, Play, Pencil, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, NotebookPen, X, CalendarClock, CheckCircle2, AlertCircle, Split, Sparkles, FileText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { DAYS, TIMES, STATUS, STATUS_ORDER } from "@/lib/constants";
import { cn } from "@/lib/utils";
import HeatmapGrid from "@/components/HeatmapGrid";
import EditableAvailability from "@/components/EditableAvailability";
import CalendarPlanner from "@/components/CalendarPlanner";
import AdminAttendance from "@/components/attendance/AdminAttendance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tennis_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) {
    return <AdminLogin onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }
  return <AdminDashboard token={token} onLogout={logout} />;
}

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      onLogin(res.data.token);
      toast.success("Accesso effettuato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali errate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4">
      <Card className="w-full max-w-md card-warm">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-md ring-1 ring-slate-200 overflow-hidden">
              <img src="/logo-ctm.png" alt="Circolo Tennis Montecchio" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <CardTitle className="text-2xl">Area Amministratore</CardTitle>
              <CardDescription>Accedi con email e password</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Email o username
              </Label>
              <Input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                autoFocus
                placeholder="admin@ctmontecchio.it"
              />
            </div>
            <div>
              <Label htmlFor="pwd" className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Password
              </Label>
              <Input
                id="pwd"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !email || !password}
              data-testid="admin-login-btn"
              className="w-full bg-[#0F172A] hover:bg-[#1e293b]"
            >
              {loading ? "Verifica..." : "Accedi"}
            </Button>
            <Link to="/" className="block text-center text-xs text-slate-500 hover:text-slate-700">
              ← Torna al modulo iscrizioni
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminDashboard({ token, onLogout }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const readOnly = role === "viewer";
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [saveGroupOpen, setSaveGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupToRename, setGroupToRename] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renamingGroup, setRenamingGroup] = useState(false);
  // Group in edit mode: after loading group, admin can modify membership
  const [editingGroup, setEditingGroup] = useState(null); // Group object
  const [savingGroupMembers, setSavingGroupMembers] = useState(false);
  // Sort state
  const [sortKey, setSortKey] = useState("created_at"); // one of: created_at | figlio | citta | green
  const [sortDir, setSortDir] = useState("desc");
  // Detail dialog edit state
  const [editOverride, setEditOverride] = useState({});
  const [editAdminNote, setEditAdminNote] = useState("");
  const [savingReg, setSavingReg] = useState(false);
  const [resettingReg, setResettingReg] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const [subgroupOpen, setSubgroupOpen] = useState(false);
  const [subgroupSource, setSubgroupSource] = useState(null);  // group being split
  const [subgroupCfg, setSubgroupCfg] = useState({ min_size: 3, max_size: 6, session_duration_minutes: 60, include_yellow: true });
  const [subgroupResult, setSubgroupResult] = useState(null);
  const [subgroupLoading, setSubgroupLoading] = useState(false);

  // Reverse map: regId -> [groups]
  const groupsByReg = useMemo(() => {
    const map = {};
    groups.forEach((g) => {
      (g.registration_ids || []).forEach((rid) => {
        (map[rid] = map[rid] || []).push(g);
      });
    });
    return map;
  }, [groups]);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [regRes, grpRes] = await Promise.all([
        axios.get(`${API}/admin/registrations`, authHeader),
        axios.get(`${API}/admin/groups`, authHeader),
      ]);
      setRegistrations(regRes.data);
      setGroups(grpRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sessione scaduta");
        onLogout();
      } else {
        toast.error("Errore nel caricamento");
      }
    } finally {
      setLoading(false);
    }
  };

  const reloadGroups = async () => {
    try {
      const res = await axios.get(`${API}/admin/groups`, authHeader);
      setGroups(res.data);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Fetch role on mount to know if this is a read-only viewer.
  useEffect(() => {
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRole(r.data?.role || "admin"))
      .catch(() => setRole("admin"));
  }, [token]);

  // If viewer, install a global axios interceptor that blocks any write request
  // (POST/PUT/PATCH/DELETE) with a toast. Keeps the UI usable read-only.
  useEffect(() => {
    if (!readOnly) return undefined;
    const id = axios.interceptors.request.use((cfg) => {
      const m = (cfg.method || "get").toLowerCase();
      if (m !== "get" && m !== "head") {
        toast.error("Accesso in sola lettura: modifica non consentita");
        return Promise.reject(new axios.Cancel("readonly"));
      }
      return cfg;
    });
    return () => axios.interceptors.request.eject(id);
  }, [readOnly]);

  const filtered = useMemo(() => {
    if (!query.trim()) return registrations;
    const q = query.toLowerCase();
    return registrations.filter((r) =>
      [
        r.genitore?.nome, r.genitore?.cognome, r.genitore?.email, r.genitore?.telefono,
        r.figlio?.nome, r.figlio?.cognome, r.figlio?.citta, r.figlio?.codice_fiscale,
      ].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [registrations, query]);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/export`, {
        ...authHeader,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `iscrizioni_tennis_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export CSV scaricato");
    } catch {
      toast.error("Errore durante l'export");
    }
  };

  const importCSV = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/registrations/import-csv`, fd, {
        headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      if (res.data.imported > 0) toast.success(`Importate ${res.data.imported} iscrizioni`);
      if (res.data.duplicates?.length) toast.info(`${res.data.duplicates.length} duplicati saltati`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} righe con errori`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante l'import");
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const openSubgroupSuggest = (group) => {
    setSubgroupSource(group);
    setSubgroupResult(null);
    setSubgroupOpen(true);
  };

  const runSubgroupSuggest = async () => {
    if (!subgroupSource) return;
    setSubgroupLoading(true);
    setSubgroupResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/groups/${subgroupSource.id}/suggest-subgroups`,
        subgroupCfg,
        authHeader,
      );
      setSubgroupResult(res.data);
      if (!res.data.subgroups?.length) toast.info("Nessuna suddivisione trovata con questi parametri");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel calcolo dei sottogruppi");
    } finally {
      setSubgroupLoading(false);
    }
  };

  const createSubgroupAsGroup = async (suggestion, index) => {
    if (!subgroupSource) return;
    const baseName = subgroupSource.nome;
    const suffix = String.fromCharCode(65 + index); // A, B, C…
    const dayLabels = { lun: "Lun", mar: "Mar", mer: "Mer", gio: "Gio", ven: "Ven" };
    const nome = `${baseName} · ${suffix} (${dayLabels[suggestion.day]} ${suggestion.start_time})`;
    try {
      await axios.post(
        `${API}/admin/groups`,
        { nome, registration_ids: suggestion.members.map((m) => m.id) },
        authHeader,
      );
      toast.success(`Gruppo "${nome}" creato`);
      await reloadGroups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/admin/registrations/${toDelete.id}`, authHeader);
      toast.success("Iscrizione eliminata");
      setToDelete(null);
      load();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const stats = useMemo(() => {
    const total = registrations.length;
    const withNotes = registrations.filter((r) => (r.note || "").trim() !== "").length;
    const last = registrations[0]?.created_at;
    return { total, withNotes, last };
  }, [registrations]);

  const toggleCheck = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredChecked = filtered.length > 0 && filtered.every((r) => checkedIds.has(r.id));
  const someFilteredChecked = filtered.some((r) => checkedIds.has(r.id));

  const toggleAllFiltered = () => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredChecked) {
        filtered.forEach((r) => next.delete(r.id));
      } else {
        filtered.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const clearSelection = () => setCheckedIds(new Set());

  const selectedRegistrations = registrations.filter((r) => checkedIds.has(r.id));

  const openSaveGroup = () => {
    setNewGroupName("");
    setSaveGroupOpen(true);
  };

  const saveGroup = async () => {
    const nome = newGroupName.trim();
    if (!nome) {
      toast.error("Inserisci un nome per il gruppo");
      return;
    }
    if (checkedIds.size === 0) {
      toast.error("Seleziona almeno un iscritto");
      return;
    }
    setSavingGroup(true);
    try {
      await axios.post(
        `${API}/admin/groups`,
        { nome, registration_ids: Array.from(checkedIds) },
        authHeader,
      );
      toast.success(`Gruppo "${nome}" salvato`);
      setSaveGroupOpen(false);
      await reloadGroups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingGroup(false);
    }
  };

  const loadGroup = (group) => {
    const validIds = group.registration_ids.filter((id) =>
      registrations.some((r) => r.id === id),
    );
    setCheckedIds(new Set(validIds));
    const missing = group.registration_ids.length - validIds.length;
    if (missing > 0) {
      toast.info(`Gruppo caricato — ${missing} iscritti non più presenti`);
    } else {
      toast.success(`Gruppo "${group.nome}" caricato (${validIds.length})`);
    }
    setQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEditGroup = (group) => {
    loadGroup(group);
    setEditingGroup(group);
  };

  const saveGroupMembers = async () => {
    if (!editingGroup) return;
    if (checkedIds.size === 0) {
      toast.error("Il gruppo deve contenere almeno un iscritto");
      return;
    }
    setSavingGroupMembers(true);
    try {
      await axios.patch(
        `${API}/admin/groups/${editingGroup.id}`,
        { registration_ids: Array.from(checkedIds) },
        authHeader,
      );
      toast.success(`Gruppo "${editingGroup.nome}" aggiornato (${checkedIds.size})`);
      setEditingGroup(null);
      await reloadGroups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSavingGroupMembers(false);
    }
  };

  const cancelEditGroup = () => {
    setEditingGroup(null);
    clearSelection();
  };

  const openGroupHeatmap = (group) => {
    loadGroup(group);
    setTimeout(() => setHeatmapOpen(true), 50);
  };

  const doDeleteGroup = async () => {
    if (!groupToDelete) return;
    try {
      await axios.delete(`${API}/admin/groups/${groupToDelete.id}`, authHeader);
      toast.success("Gruppo eliminato");
      setGroupToDelete(null);
      await reloadGroups();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openRenameGroup = (group) => {
    setGroupToRename(group);
    setRenameValue(group.nome);
  };

  const doRenameGroup = async () => {
    if (!groupToRename) return;
    const nome = renameValue.trim();
    if (!nome) {
      toast.error("Inserisci un nome");
      return;
    }
    if (nome === groupToRename.nome) {
      setGroupToRename(null);
      return;
    }
    setRenamingGroup(true);
    try {
      await axios.patch(
        `${API}/admin/groups/${groupToRename.id}`,
        { nome },
        authHeader,
      );
      toast.success(`Gruppo rinominato in "${nome}"`);
      setGroupToRename(null);
      await reloadGroups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella rinomina");
    } finally {
      setRenamingGroup(false);
    }
  };

  // Sortable helpers
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedFiltered = useMemo(() => {
    const getEffective = (r) => ({ ...(r.disponibilita || {}), ...(r.disponibilita_admin || {}) });
    const arr = [...filtered];
    const cmp = (a, b) => {
      let av, bv;
      switch (sortKey) {
        case "figlio":
          av = `${a.figlio.cognome} ${a.figlio.nome}`.toLowerCase();
          bv = `${b.figlio.cognome} ${b.figlio.nome}`.toLowerCase();
          break;
        case "citta":
          av = (a.figlio.citta || "").toLowerCase();
          bv = (b.figlio.citta || "").toLowerCase();
          break;
        case "green":
          av = Object.values(getEffective(a)).filter((s) => s === "green").length;
          bv = Object.values(getEffective(b)).filter((s) => s === "green").length;
          break;
        case "created_at":
        default:
          av = a.created_at || "";
          bv = b.created_at || "";
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    };
    arr.sort(cmp);
    return arr;
  }, [filtered, sortKey, sortDir]);

  // Detail dialog: init state when selecting
  useEffect(() => {
    if (selected) {
      setEditOverride({ ...(selected.disponibilita_admin || {}) });
      setEditAdminNote(selected.admin_note || "");
    } else {
      setEditOverride({});
      setEditAdminNote("");
    }
  }, [selected]);

  const hasEditChanges = useMemo(() => {
    if (!selected) return false;
    const origOverride = selected.disponibilita_admin || {};
    const origNote = selected.admin_note || "";
    const sameNote = origNote === editAdminNote;
    const keysA = Object.keys(origOverride);
    const keysB = Object.keys(editOverride);
    const sameOverride =
      keysA.length === keysB.length &&
      keysA.every((k) => origOverride[k] === editOverride[k]);
    return !(sameNote && sameOverride);
  }, [selected, editOverride, editAdminNote]);

  const saveRegChanges = async () => {
    if (!selected) return;
    setSavingReg(true);
    try {
      const res = await axios.patch(
        `${API}/admin/registrations/${selected.id}`,
        { disponibilita_admin: editOverride, admin_note: editAdminNote },
        authHeader,
      );
      // Update local list and selected
      setRegistrations((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
      setSelected(res.data);
      toast.success("Modifiche salvate");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingReg(false);
    }
  };

  const resetRegOverrides = async () => {
    if (!selected) return;
    setResettingReg(true);
    try {
      const res = await axios.patch(
        `${API}/admin/registrations/${selected.id}`,
        { disponibilita_admin: {} },
        authHeader,
      );
      setRegistrations((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
      setSelected(res.data);
      setEditOverride({});
      toast.success("Disponibilità ripristinata a come inserita dal genitore");
    } catch {
      toast.error("Errore nel ripristino");
    } finally {
      setResettingReg(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/85 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-md ring-1 ring-slate-200 overflow-hidden">
              <img src="/logo-ctm.png" alt="Circolo Tennis Montecchio" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <div className="font-bold text-slate-900 leading-tight">Admin Dashboard</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Circolo Tennis Montecchio</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {readOnly && (
              <span
                data-testid="readonly-badge"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider"
                title="Accesso in sola lettura"
              >
                <Eye className="w-3.5 h-3.5" />
                Sola lettura
              </span>
            )}
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-1.5">
                <ArrowLeft className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Modulo</span>
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-btn" className="gap-1.5">
              <LogOut className="w-3.5 h-3.5" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {readOnly && (
          <div
            data-testid="readonly-banner"
            className="mb-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-4 py-2 text-sm flex items-center gap-2"
          >
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span>
              Sei collegato in <strong>sola lettura</strong>: puoi consultare i dati ma non modificare, importare o generare il calendario.
            </span>
          </div>
        )}
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Iscrizioni totali" value={stats.total} icon={Users} accent="#15803D" testid="stat-total" />
          <StatCard label="Con note" value={stats.withNotes} icon={Eye} accent="#C85A32" testid="stat-notes" />
          <StatCard
            label="Ultima iscrizione"
            value={stats.last ? new Date(stats.last).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
            icon={Trophy}
            accent="#D97706"
            testid="stat-last"
            small
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca per nome, email, città..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Button
            onClick={exportCSV}
            data-testid="export-csv-btn"
            className="bg-[#15803D] hover:bg-[#166534] gap-2"
          >
            <Download className="w-4 h-4" />
            Esporta CSV
          </Button>
          {!readOnly && (
            <Button
              onClick={() => setImportOpen(true)}
              data-testid="import-csv-btn"
              variant="outline"
              className="gap-2 border-[#15803D] text-[#15803D] hover:bg-[#15803D]/10"
            >
              <Upload className="w-4 h-4" />
              Importa CSV
            </Button>
          )}
          <Button
            onClick={() => setPlannerOpen(true)}
            data-testid="open-planner-btn"
            className="bg-[#0F172A] hover:bg-[#1e293b] gap-2"
          >
            <CalendarClock className="w-4 h-4" />
            Calendario corsi
          </Button>
          <Button
            onClick={() => setAttendanceOpen(true)}
            data-testid="open-attendance-btn"
            className="bg-[#C85A32] hover:bg-[#a94a28] gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            Presenze
          </Button>
        </div>
        {attendanceOpen && (
          <AdminAttendance
            token={token}
            registrations={registrations}
            groups={groups}
            readOnly={readOnly}
            onClose={() => setAttendanceOpen(false)}
          />
        )}

        {/* Selection bar */}
        {checkedIds.size > 0 && (
          <div
            data-testid="selection-bar"
            className={cn(
              "mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-2xl px-4 py-3 card-warm",
              editingGroup
                ? "border border-amber-300 bg-amber-50"
                : "border border-[#C85A32]/30 bg-[#FDF6F0]",
            )}
          >
            <div className="text-sm text-slate-700">
              {editingGroup ? (
                <>
                  Stai modificando il gruppo{" "}
                  <strong>&laquo;{editingGroup.nome}&raquo;</strong> — spunta/togli iscritti dalla tabella, poi salva.{" "}
                  <span className="text-slate-500 font-mono">({checkedIds.size} attualmente)</span>
                </>
              ) : (
                <>
                  <strong data-testid="selection-count">{checkedIds.size}</strong>{" "}
                  {checkedIds.size === 1 ? "iscritto selezionato" : "iscritti selezionati"} — genera la heatmap o salva come gruppo riutilizzabile.
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {editingGroup ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={cancelEditGroup}
                    data-testid="cancel-edit-group-btn"
                    className="text-slate-600"
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={saveGroupMembers}
                    disabled={savingGroupMembers || checkedIds.size === 0}
                    data-testid="save-group-members-btn"
                    className="bg-amber-600 hover:bg-amber-700 gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {savingGroupMembers ? "Salvataggio..." : "Salva modifiche"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                    data-testid="clear-selection-btn"
                    className="text-slate-600"
                  >
                    Deseleziona
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openSaveGroup}
                    data-testid="save-group-btn"
                    className="gap-1.5 border-[#C85A32]/40 text-[#C85A32] hover:bg-[#C85A32]/10 hover:text-[#C85A32]"
                  >
                    <Save className="w-3.5 h-3.5" />
                    Salva gruppo
                  </Button>
                  <Button
                    onClick={() => setHeatmapOpen(true)}
                    data-testid="open-heatmap-btn"
                    className="bg-[#C85A32] hover:bg-[#a94a28] gap-2"
                  >
                    <LayoutGrid className="w-4 h-4" />
                    Heatmap ({checkedIds.size})
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Saved groups */}
        {groups.length > 0 && (
          <div className="mb-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 card-warm" data-testid="saved-groups">
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen className="w-4 h-4 text-slate-500" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Gruppi salvati ({groups.length})
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {groups.map((g) => (
                <div
                  key={g.id}
                  data-testid={`group-chip-${g.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 pl-3 pr-1 py-1"
                >
                  <span className="text-sm font-semibold text-slate-800">{g.nome}</span>
                  <span className="text-[10px] font-mono text-slate-500 ml-1">
                    {g.registration_ids.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => loadGroup(g)}
                    data-testid={`group-load-${g.id}`}
                    title="Carica selezione"
                    className="ml-1 w-6 h-6 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-600"
                  >
                    <FolderOpen className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => startEditGroup(g)}
                    data-testid={`group-edit-${g.id}`}
                    title="Modifica membri del gruppo"
                    className="w-6 h-6 rounded-full hover:bg-amber-100 flex items-center justify-center text-amber-700"
                  >
                    <Users className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openGroupHeatmap(g)}
                    data-testid={`group-heatmap-${g.id}`}
                    title="Apri heatmap del gruppo"
                    className="w-6 h-6 rounded-full hover:bg-[#C85A32]/10 flex items-center justify-center text-[#C85A32]"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </button>
                  {g.registration_ids.length >= 4 && (
                    <button
                      type="button"
                      onClick={() => openSubgroupSuggest(g)}
                      data-testid={`group-split-${g.id}`}
                      title="Suggerisci sottogruppi in base alle disponibilità"
                      className="w-6 h-6 rounded-full hover:bg-indigo-100 flex items-center justify-center text-indigo-700"
                    >
                      <Split className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openRenameGroup(g)}
                    data-testid={`group-rename-${g.id}`}
                    title="Rinomina gruppo"
                    className="w-6 h-6 rounded-full hover:bg-amber-50 flex items-center justify-center text-amber-600"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setGroupToDelete(g)}
                    data-testid={`group-delete-${g.id}`}
                    title="Elimina gruppo"
                    className="w-6 h-6 rounded-full hover:bg-rose-50 flex items-center justify-center text-rose-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <Card className="card-warm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Caricamento...</div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">
                  {registrations.length === 0 ? "Nessuna iscrizione ancora ricevuta." : "Nessun risultato per la ricerca."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="registrations-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="pl-4 pr-2 py-3 w-10">
                        <Checkbox
                          checked={allFilteredChecked}
                          onCheckedChange={toggleAllFiltered}
                          data-testid="select-all-checkbox"
                          aria-label="Seleziona tutti"
                          className={cn(
                            !allFilteredChecked && someFilteredChecked && "data-[state=unchecked]:bg-slate-300"
                          )}
                        />
                      </th>
                      <th className="px-4 py-3 font-semibold">
                        <SortHeader label="Figlio/a" col="figlio" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                      </th>
                      <th className="px-4 py-3 font-semibold hidden md:table-cell">Genitore</th>
                      <th className="px-4 py-3 font-semibold hidden lg:table-cell">Contatto</th>
                      <th className="px-4 py-3 font-semibold">
                        <SortHeader label="Disp." col="green" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} title="Ordina per numero di disponibilità verde" />
                      </th>
                      <th className="px-4 py-3 font-semibold hidden md:table-cell">
                        <SortHeader label="Data" col="created_at" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                      </th>
                      <th className="px-4 py-3 font-semibold text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFiltered.map((r) => {
                      const effective = { ...(r.disponibilita || {}), ...(r.disponibilita_admin || {}) };
                      const counts = countStatus(effective);
                      const hasAdminChanges = Object.keys(r.disponibilita_admin || {}).length > 0 || (r.admin_note || "").trim() !== "";
                      const isChecked = checkedIds.has(r.id);
                      return (
                        <tr
                          key={r.id}
                          className={cn(
                            "border-b border-slate-100 hover:bg-slate-50",
                            isChecked && "bg-[#FDF6F0]/60 hover:bg-[#FDF6F0]"
                          )}
                          data-testid={`reg-row-${r.id}`}
                        >
                          <td className="pl-4 pr-2 py-3">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggleCheck(r.id)}
                              data-testid={`select-checkbox-${r.id}`}
                              aria-label={`Seleziona ${r.figlio.nome}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                              {r.figlio.nome} {r.figlio.cognome}
                              {hasAdminChanges && (
                                <span
                                  title="Contiene modifiche dell'admin"
                                  className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded"
                                >
                                  <NotebookPen className="w-2.5 h-2.5" /> mod
                                </span>
                              )}
                              {(groupsByReg[r.id] || []).map((g) => (
                                <span
                                  key={g.id}
                                  title={`Gruppo: ${g.nome}`}
                                  data-testid={`row-group-chip-${r.id}-${g.id}`}
                                  className="inline-flex items-center text-[9px] font-bold uppercase tracking-wider text-white px-1.5 py-0.5 rounded"
                                  style={{ background: g.color || "#64748b" }}
                                >
                                  {g.nome}
                                </span>
                              ))}
                            </div>
                            <div className="text-xs text-slate-500">{r.figlio.citta} · {formatDate(r.figlio.data_nascita)}</div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="text-slate-800">{r.genitore.nome} {r.genitore.cognome}</div>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <div className="text-xs text-slate-700">{r.genitore.email}</div>
                            <div className="text-xs text-slate-500 font-mono">{r.genitore.telefono}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {STATUS_ORDER.map((k) => (
                                <span key={k} className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded", STATUS[k].bgLight, STATUS[k].text)}>
                                  {counts[k]}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-500 whitespace-nowrap">
                            {new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setSelected(r)} data-testid={`view-btn-${r.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(
                                      `${API}/admin/registrations/${r.id}/module.pdf`,
                                      { ...authHeader, responseType: "blob" },
                                    );
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = `iscrizione_${r.figlio.cognome}_${r.figlio.nome}.pdf`.replace(/\s+/g, "_");
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                  } catch (e) {
                                    toast.error("Errore nel generare il modulo PDF");
                                  }
                                }}
                                data-testid={`module-pdf-btn-${r.id}`}
                                title="Scarica modulo iscrizione (PDF)"
                                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              {!readOnly && (
                                <Button size="sm" variant="ghost" onClick={() => setToDelete(r)} data-testid={`delete-btn-${r.id}`} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Rename group dialog */}
      <Dialog open={!!groupToRename} onOpenChange={(o) => !o && setGroupToRename(null)}>
        <DialogContent className="max-w-md" data-testid="rename-group-dialog">
          <DialogHeader>
            <DialogTitle>Rinomina gruppo</DialogTitle>
            <DialogDescription>
              {groupToRename && `Modifica il nome di "${groupToRename.nome}".`}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); doRenameGroup(); }}
            className="space-y-4 mt-2"
          >
            <div>
              <Label htmlFor="rename-input" className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Nuovo nome
              </Label>
              <Input
                id="rename-input"
                data-testid="rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setGroupToRename(null)}>
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={renamingGroup || !renameValue.trim()}
                data-testid="confirm-rename-group-btn"
                className="bg-[#C85A32] hover:bg-[#a94a28]"
              >
                {renamingGroup ? "Salvataggio..." : "Salva"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Save group dialog */}
      <Dialog open={saveGroupOpen} onOpenChange={setSaveGroupOpen}>
        <DialogContent className="max-w-md" data-testid="save-group-dialog">
          <DialogHeader>
            <DialogTitle>Salva selezione come gruppo</DialogTitle>
            <DialogDescription>
              Dai un nome a questo gruppo di {checkedIds.size} iscritti per ritrovarlo velocemente.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); saveGroup(); }}
            className="space-y-4 mt-2"
          >
            <div>
              <Label htmlFor="group-name" className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Nome gruppo
              </Label>
              <Input
                id="group-name"
                data-testid="group-name-input"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Es. Under 10 - Martedì"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setSaveGroupOpen(false)}>
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={savingGroup || !newGroupName.trim()}
                data-testid="confirm-save-group-btn"
                className="bg-[#C85A32] hover:bg-[#a94a28]"
              >
                {savingGroup ? "Salvataggio..." : "Salva gruppo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete group confirm */}
      <AlertDialog open={!!groupToDelete} onOpenChange={(o) => !o && setGroupToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo gruppo?</AlertDialogTitle>
            <AlertDialogDescription>
              {groupToDelete && `"${groupToDelete.nome}" verrà rimosso. Gli iscritti restano intatti.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteGroup} data-testid="confirm-delete-group-btn" className="bg-rose-600 hover:bg-rose-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Heatmap modal */}
      <Dialog open={heatmapOpen} onOpenChange={setHeatmapOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="heatmap-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Heatmap sovrapposizioni</DialogTitle>
            <DialogDescription>
              Analisi delle fasce orarie condivise dagli iscritti selezionati per formare un gruppo del corso.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Gruppo ({selectedRegistrations.length})
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="heatmap-group-list">
              {selectedRegistrations.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-xs px-2.5 py-1 border border-slate-200"
                >
                  {r.figlio.nome} {r.figlio.cognome}
                </span>
              ))}
            </div>
          </div>

          <HeatmapGrid registrations={selectedRegistrations} />
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {selected.figlio.nome} {selected.figlio.cognome}
                </DialogTitle>
                <DialogDescription>
                  Iscritto il {new Date(selected.created_at).toLocaleString("it-IT")}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Genitore</h3>
                  <div className="space-y-1 text-sm">
                    <div><strong>{selected.genitore.nome} {selected.genitore.cognome}</strong></div>
                    <div className="text-slate-600">{selected.genitore.email}</div>
                    <div className="text-slate-600 font-mono">{selected.genitore.telefono}</div>
                  </div>
                </section>
                <section>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Figlio/a</h3>
                  <div className="space-y-1 text-sm">
                    <div>Nato/a il <strong>{formatDate(selected.figlio.data_nascita)}</strong></div>
                    <div className="text-slate-600 font-mono text-xs">CF: {selected.figlio.codice_fiscale}</div>
                    <div className="text-slate-600 text-xs">
                      {selected.figlio.indirizzo}, {selected.figlio.cap} {selected.figlio.citta} ({selected.figlio.provincia})
                    </div>
                  </div>
                </section>
              </div>

              {selected.note && (
                <section className="mt-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Note</h3>
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                    {selected.note}
                  </div>
                </section>
              )}

              {(groupsByReg[selected.id] || []).length > 0 && (
                <section className="mt-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Gruppi di appartenenza
                  </h3>
                  <div className="flex flex-wrap gap-1.5" data-testid="detail-groups">
                    {(groupsByReg[selected.id] || []).map((g) => (
                      <span
                        key={g.id}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full text-white"
                        style={{ background: g.color || "#64748b" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
                        {g.nome}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              <section className="mt-4">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Disponibilità (modificabile)
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={resetRegOverrides}
                      disabled={resettingReg || Object.keys(selected.disponibilita_admin || {}).length === 0}
                      data-testid="reset-availability-btn"
                      className="gap-1.5 text-slate-600"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Ripristina originale
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveRegChanges}
                      disabled={savingReg || !hasEditChanges}
                      data-testid="save-availability-btn"
                      className="bg-[#15803D] hover:bg-[#166534] gap-1.5"
                    >
                      <Save className="w-3.5 h-3.5" />
                      {savingReg ? "Salvataggio..." : "Salva modifiche"}
                    </Button>
                  </div>
                </div>
                <EditableAvailability
                  original={selected.disponibilita}
                  override={editOverride}
                  onChange={setEditOverride}
                />
              </section>

              <section className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <NotebookPen className="w-3.5 h-3.5 text-slate-500" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Note personali (visibili solo a te)
                  </h3>
                </div>
                <Textarea
                  data-testid="admin-note-textarea"
                  value={editAdminNote}
                  onChange={(e) => setEditAdminNote(e.target.value)}
                  placeholder="Appunti su questo iscritto (contatti fatti, decisioni, gruppo assegnato...)"
                  rows={3}
                  className="bg-sky-50/50 border-sky-200 focus-visible:ring-sky-400"
                />
              </section>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Suggest Subgroups Dialog */}
      <Dialog open={subgroupOpen} onOpenChange={(o) => { setSubgroupOpen(o); if (!o) { setSubgroupSource(null); setSubgroupResult(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="subgroup-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Split className="w-5 h-5 text-indigo-600" />
              Suggerisci sottogruppi
            </DialogTitle>
            <DialogDescription>
              Da <strong>"{subgroupSource?.nome}"</strong> ({subgroupSource?.registration_ids?.length || 0} iscritti). L'algoritmo cerca fasce orarie con abbastanza allievi disponibili contemporaneamente.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Min. allievi</Label>
              <Input type="number" min="1" max="20" value={subgroupCfg.min_size}
                onChange={(e) => setSubgroupCfg((c) => ({ ...c, min_size: Math.max(1, parseInt(e.target.value) || 1) }))}
                data-testid="subgroup-min-size" className="h-8" />
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Max. allievi</Label>
              <Input type="number" min="1" max="50" value={subgroupCfg.max_size}
                onChange={(e) => setSubgroupCfg((c) => ({ ...c, max_size: Math.max(1, parseInt(e.target.value) || 1) }))}
                data-testid="subgroup-max-size" className="h-8" />
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Durata (min)</Label>
              <select value={subgroupCfg.session_duration_minutes}
                onChange={(e) => setSubgroupCfg((c) => ({ ...c, session_duration_minutes: parseInt(e.target.value) }))}
                data-testid="subgroup-duration"
                className="h-8 w-full rounded-md border border-slate-300 bg-white text-sm px-2">
                <option value="30">30</option><option value="60">60</option><option value="90">90</option><option value="120">120</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">"In alternativa"</Label>
              <label className="flex items-center gap-2 h-8">
                <Checkbox checked={subgroupCfg.include_yellow}
                  onCheckedChange={(v) => setSubgroupCfg((c) => ({ ...c, include_yellow: !!v }))}
                  data-testid="subgroup-include-yellow" />
                <span className="text-xs">Tollera</span>
              </label>
            </div>
          </div>

          <Button
            onClick={runSubgroupSuggest}
            disabled={subgroupLoading}
            data-testid="subgroup-run-btn"
            className="w-full bg-indigo-600 hover:bg-indigo-700 gap-2 mt-2"
          >
            <Sparkles className="w-4 h-4" />
            {subgroupLoading ? "Calcolo…" : "Calcola suggerimenti"}
          </Button>

          {subgroupResult && (
            <div className="space-y-3 mt-3" data-testid="subgroup-result">
              <div className="text-xs text-slate-600 flex items-center justify-between">
                <span><strong>{subgroupResult.subgroups.length}</strong> sottogrupp{subgroupResult.subgroups.length === 1 ? "o" : "i"} trovat{subgroupResult.subgroups.length === 1 ? "o" : "i"} su {subgroupResult.total_members} iscritti</span>
                {subgroupResult.unassigned.length > 0 && (
                  <span className="text-amber-700 font-semibold">{subgroupResult.unassigned.length} non collocati</span>
                )}
              </div>

              {subgroupResult.subgroups.map((sg, i) => (
                <div key={i} className="rounded-lg border-2 border-indigo-100 bg-indigo-50/40 p-3" data-testid={`subgroup-card-${i}`}>
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div>
                      <div className="text-sm font-bold text-indigo-900">
                        Sottogruppo {String.fromCharCode(65 + i)} · {sg.members.length} allievi
                      </div>
                      <div className="text-xs text-indigo-700 flex items-center gap-1.5 mt-0.5">
                        <CalendarClock className="w-3.5 h-3.5" />
                        {({ lun: "Lunedì", mar: "Martedì", mer: "Mercoledì", gio: "Giovedì", ven: "Venerdì" })[sg.day]} · {sg.start_time}–{sg.end_time}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 text-xs">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">
                          {sg.green_count} <CheckCircle2 className="w-3 h-3" />
                        </span>
                        {sg.yellow_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold">
                            {sg.yellow_count} ⚠
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => createSubgroupAsGroup(sg, i)}
                        data-testid={`subgroup-create-${i}`}
                        className="bg-indigo-600 hover:bg-indigo-700 h-8 gap-1"
                      >
                        <Save className="w-3.5 h-3.5" />
                        Crea gruppo
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {sg.members.map((m) => (
                      <span key={m.id} className={cn(
                        "text-xs px-2 py-0.5 rounded-full font-semibold border",
                        m.status === "green"
                          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                          : "bg-amber-50 text-amber-800 border-amber-200"
                      )}>
                        {m.nome} {m.cognome}
                      </span>
                    ))}
                  </div>
                </div>
              ))}

              {subgroupResult.unassigned.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="text-sm font-bold text-amber-900 mb-1 flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4" />
                    Non collocati ({subgroupResult.unassigned.length})
                  </div>
                  <div className="text-[11px] text-amber-800/80 mb-1.5">
                    Nessuno slot con abbastanza colleghi disponibili — servono più iscritti o parametri più permissivi.
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {subgroupResult.unassigned.map((m) => (
                      <span key={m.id} className="text-xs px-2 py-0.5 rounded-full bg-white text-amber-900 border border-amber-200 font-semibold">
                        {m.nome} {m.cognome}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="import-csv-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#15803D]" />
              Importa iscrizioni da CSV
            </DialogTitle>
            <DialogDescription>
              Carica un file .csv (separatore <code className="text-xs">;</code>). Il formato accettato è quello dell'export.
              I duplicati vengono rilevati automaticamente in base al <strong>Codice Fiscale</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center bg-slate-50">
              <input
                ref={importInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => e.target.files?.[0] && importCSV(e.target.files[0])}
                className="hidden"
                data-testid="import-csv-file-input"
              />
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 mb-2">
                Trascina qui il file o clicca per selezionarlo
              </p>
              <Button
                onClick={() => importInputRef.current?.click()}
                disabled={importing}
                data-testid="import-csv-select-btn"
                className="bg-[#15803D] hover:bg-[#166534]"
              >
                {importing ? "Importazione…" : "Seleziona file CSV"}
              </Button>
            </div>

            <details className="text-xs text-slate-500 rounded-lg border border-slate-200 p-2">
              <summary className="cursor-pointer font-semibold text-slate-700">Colonne accettate</summary>
              <div className="mt-2 font-mono leading-relaxed">
                Genitore Nome, Genitore Cognome, Genitore Email, Genitore Telefono,<br/>
                Figlio Nome, Figlio Cognome, Figlio Data Nascita, <strong>Codice Fiscale</strong>,<br/>
                Indirizzo, Città, CAP, Provincia, Note, <strong>Gruppi</strong><br/>
                <span className="text-slate-400">
                  (Gruppi: nomi separati da virgola; se non esistono vengono creati automaticamente. Colonne di disponibilità opzionali, formato "Sì" / "In alternativa" / "No")
                </span>
              </div>
            </details>

            {importResult && (
              <div className="space-y-2" data-testid="import-csv-result">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-center">
                    <CheckCircle2 className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                    <div className="text-xl font-bold text-emerald-900">{importResult.imported}</div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Importate</div>
                  </div>
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center">
                    <AlertCircle className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                    <div className="text-xl font-bold text-amber-900">{importResult.duplicates?.length || 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Duplicati</div>
                  </div>
                  <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-center">
                    <X className="w-5 h-5 mx-auto text-rose-600 mb-1" />
                    <div className="text-xl font-bold text-rose-900">{importResult.errors?.length || 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">Errori</div>
                  </div>
                </div>

                {importResult.groups_created?.length > 0 && (
                  <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-2">
                    <div className="text-[11px] font-bold text-indigo-900 mb-1">Gruppi creati automaticamente:</div>
                    <div className="flex flex-wrap gap-1">
                      {importResult.groups_created.map((g, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-semibold">
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {importResult.duplicates?.length > 0 && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 max-h-32 overflow-y-auto">
                    <div className="text-[11px] font-bold text-amber-900 mb-1">Codici fiscali già presenti:</div>
                    <ul className="text-xs text-amber-900 space-y-0.5">
                      {importResult.duplicates.map((d, i) => (
                        <li key={i} className="flex justify-between gap-2">
                          <span className="truncate">{d.nome || "(senza nome)"}</span>
                          <span className="font-mono opacity-75">{d.codice_fiscale}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {importResult.errors?.length > 0 && (
                  <div className="rounded-lg bg-rose-50 border border-rose-200 p-2 max-h-32 overflow-y-auto">
                    <div className="text-[11px] font-bold text-rose-900 mb-1">Errori:</div>
                    <ul className="text-xs text-rose-900 space-y-0.5">
                      {importResult.errors.map((e, i) => (
                        <li key={i}>
                          Riga {e.row}: {e.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa iscrizione?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && `${toDelete.figlio?.nome} ${toDelete.figlio?.cognome} — L'operazione non è reversibile.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn" className="bg-rose-600 hover:bg-rose-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Calendar Planner */}
      <CalendarPlanner
        open={plannerOpen}
        onOpenChange={setPlannerOpen}
        token={token}
        groups={groups}
        registrations={registrations}
        reloadGroups={reloadGroups}
        readOnly={readOnly}
      />
    </div>
  );
}

function SortHeader({ label, col, sortKey, sortDir, onToggle, title }) {
  const active = sortKey === col;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onToggle(col)}
      title={title || `Ordina per ${label}`}
      data-testid={`sort-${col}`}
      className={cn(
        "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider hover:text-slate-900 transition-colors",
        active ? "text-slate-900" : "text-slate-500"
      )}
    >
      {label}
      <Icon className={cn("w-3 h-3", active ? "opacity-100" : "opacity-40")} />
    </button>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid, small }) {
  return (
    <Card className="card-warm" data-testid={testid}>
      <CardContent className="pt-5 pb-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}18` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className={cn("font-bold text-slate-900 leading-tight", small ? "text-base" : "text-2xl")}>
            {value}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ReadOnlyGrid({ disponibilita }) {
  const map = disponibilita || {};
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-3">
      <div className="min-w-[560px]">
        <div className="grid grid-cols-[64px_repeat(5,minmax(0,1fr))] gap-1.5 mb-1.5">
          <div />
          {DAYS.map((d) => (
            <div key={d.key} className="text-[11px] font-bold uppercase tracking-wider text-slate-600 text-center">
              {d.short}
            </div>
          ))}
        </div>
        {TIMES.map((t) => (
          <div key={t} className="grid grid-cols-[64px_repeat(5,minmax(0,1fr))] gap-1.5 mb-1 items-center">
            <div className="text-[11px] font-mono text-slate-500 text-right pr-2">{t}</div>
            {DAYS.map((d) => {
              const s = map[`${d.key}_${t}`];
              return (
                <div
                  key={d.key + t}
                  className={cn("h-7 rounded", s ? STATUS[s].bg : "bg-slate-100")}
                  title={s ? STATUS[s].label : ""}
                />
              );
            })}
          </div>
        ))}
        <div className="flex gap-3 mt-3 text-[11px] text-slate-600">
          {STATUS_ORDER.map((k) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className={cn("w-2.5 h-2.5 rounded", STATUS[k].bg)} />
              {STATUS[k].label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function countStatus(disp) {
  const c = { green: 0, yellow: 0, red: 0 };
  Object.values(disp || {}).forEach((s) => c[s] !== undefined && c[s]++);
  return c;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT");
  } catch {
    return iso;
  }
}
